import React from "react";
import {
  defaultPeriodConfig,
  emptyTicketSet,
  type PricingPeriodKey,
  type TicketCategory,
  type TicketSetInput,
} from "../../api/ticketSets";
import {
  MEMBERSHIP_TIER_LABELS,
  MEMBERSHIP_TIERS,
  type MembershipTier,
} from "../../types/pricingConfig";

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value: string): string {
  return new Date(value).toISOString();
}

const CATEGORY_OPTIONS: { value: TicketCategory; label: string }[] = [
  { value: "full_conference", label: "Full conference" },
  { value: "single_day", label: "Listener / single day" },
  { value: "add_on", label: "Add-on (banquet / co-registrant)" },
  { value: "extra_page", label: "Extra page fee" },
  { value: "additional_paper", label: "Additional paper" },
  { value: null, label: "Other" },
];

interface Props {
  sets: TicketSetInput[];
  onChange: (sets: TicketSetInput[]) => void;
  eventStartDate: string;
}

export default function TicketSetsEditor({
  sets,
  onChange,
  eventStartDate,
}: Props) {
  function updateSet(index: number, patch: Partial<TicketSetInput>) {
    const next = [...sets];
    next[index] = { ...next[index]!, ...patch };
    onChange(next);
  }

  function updateType(
    setIndex: number,
    typeIndex: number,
    patch: Partial<TicketSetInput["types"][number]>,
  ) {
    const set = sets[setIndex]!;
    const types = [...set.types];
    types[typeIndex] = { ...types[typeIndex]!, ...patch };
    updateSet(setIndex, { types });
  }

  function addSet() {
    onChange([
      ...sets,
      { ...emptyTicketSet(eventStartDate), sortOrder: sets.length },
    ]);
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-medium text-slate-800">Ticket sets</p>
        <p className="text-xs text-slate-500 mt-1">
          Each set groups related ticket types. Enable registration periods to
          enter Early Bird / Standard / On-Site prices in a matrix — rates are
          stored on ticket types (not a separate event config).
        </p>
      </div>

      {sets.map((set, setIndex) => {
        const periods = set.periodConfig?.periods ?? [];
        return (
          <div
            key={set.id ?? `new-${setIndex}`}
            className="border border-slate-200 rounded-sm p-4 space-y-4"
          >
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <label className="block text-sm flex-1">
                <span className="font-medium">Ticket set name *</span>
                <input
                  className="mt-1 w-full border border-slate-300 p-2 text-sm"
                  value={set.name}
                  placeholder="e.g. Full Conference"
                  onChange={(e) => updateSet(setIndex, { name: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="text-sm text-red-600 underline"
                onClick={() => onChange(sets.filter((_, i) => i !== setIndex))}
                disabled={sets.length <= 1}
              >
                Remove set
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={set.hasPricingPeriods}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  updateSet(setIndex, {
                    hasPricingPeriods: enabled,
                    periodConfig: enabled
                      ? set.periodConfig ?? defaultPeriodConfig(eventStartDate)
                      : null,
                    types: set.types.map((t) =>
                      enabled
                        ? {
                            ...t,
                            pricesByPeriod: t.pricesByPeriod ?? {
                              early_bird: t.pricePence ?? 0,
                              standard: t.pricePence ?? 0,
                              on_site: t.pricePence ?? 0,
                            },
                            pricePence: undefined,
                          }
                        : {
                            ...t,
                            pricePence:
                              t.pricesByPeriod?.early_bird ?? t.pricePence ?? 0,
                            pricesByPeriod: undefined,
                          },
                    ),
                  });
                }}
              />
              <span>Rates vary by registration period</span>
            </label>

            {set.hasPricingPeriods && set.periodConfig && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  Period end dates
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {periods.map((period, pIndex) => (
                    <label key={period.key} className="block text-sm">
                      <span className="font-medium">
                        {period.label}
                        {period.key === "on_site" ? " (no end)" : " ends"}
                      </span>
                      {period.key === "on_site" ? (
                        <p className="mt-1 text-xs text-slate-500">
                          Applies after the previous period ends
                        </p>
                      ) : (
                        <input
                          type="datetime-local"
                          className="mt-1 w-full border border-slate-300 p-2 text-sm"
                          value={toDatetimeLocal(period.endsAt)}
                          onChange={(e) => {
                            const nextPeriods = [...periods];
                            nextPeriods[pIndex] = {
                              ...period,
                              endsAt: fromDatetimeLocal(e.target.value),
                            };
                            updateSet(setIndex, {
                              periodConfig: { periods: nextPeriods },
                            });
                          }}
                          required
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-slate-200">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="p-2 font-medium">Ticket type</th>
                    <th className="p-2 font-medium">Category</th>
                    <th className="p-2 font-medium">Membership</th>
                    {set.hasPricingPeriods
                      ? periods.map((p) => (
                          <th key={p.key} className="p-2 font-medium">
                            {p.label} (USD)
                          </th>
                        ))
                      : (
                          <th className="p-2 font-medium">Price (USD)</th>
                        )}
                    <th className="p-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {set.types.map((row, typeIndex) => (
                    <tr key={typeIndex} className="border-t border-slate-200">
                      <td className="p-2 min-w-[10rem]">
                        <input
                          className="w-full border border-slate-300 p-1.5"
                          value={row.typeName}
                          placeholder="e.g. Non-IEEE Member"
                          onChange={(e) =>
                            updateType(setIndex, typeIndex, {
                              typeName: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="p-2 min-w-[9rem]">
                        <select
                          className="w-full border border-slate-300 p-1.5"
                          value={row.category ?? ""}
                          onChange={(e) =>
                            updateType(setIndex, typeIndex, {
                              category: (e.target.value ||
                                null) as TicketCategory,
                            })
                          }
                        >
                          {CATEGORY_OPTIONS.map((opt) => (
                            <option
                              key={String(opt.value)}
                              value={opt.value ?? ""}
                            >
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2 min-w-[9rem]">
                        <select
                          className="w-full border border-slate-300 p-1.5"
                          value={row.membershipTier ?? ""}
                          onChange={(e) =>
                            updateType(setIndex, typeIndex, {
                              membershipTier: (e.target.value ||
                                null) as MembershipTier | null,
                            })
                          }
                        >
                          <option value="">None</option>
                          {MEMBERSHIP_TIERS.map((tier) => (
                            <option key={tier} value={tier}>
                              {MEMBERSHIP_TIER_LABELS[tier]}
                            </option>
                          ))}
                        </select>
                      </td>
                      {set.hasPricingPeriods
                        ? periods.map((p) => (
                            <td key={p.key} className="p-2">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-full min-w-[5rem] border border-slate-300 p-1.5"
                                value={
                                  (row.pricesByPeriod?.[
                                    p.key as PricingPeriodKey
                                  ] ?? 0) / 100 || ""
                                }
                                onChange={(e) => {
                                  const usd = Number(e.target.value) || 0;
                                  updateType(setIndex, typeIndex, {
                                    pricesByPeriod: {
                                      ...row.pricesByPeriod,
                                      [p.key]: Math.round(usd * 100),
                                    },
                                  });
                                }}
                              />
                            </td>
                          ))
                        : (
                            <td className="p-2">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-full min-w-[5rem] border border-slate-300 p-1.5"
                                value={(row.pricePence ?? 0) / 100 || ""}
                                onChange={(e) =>
                                  updateType(setIndex, typeIndex, {
                                    pricePence: Math.round(
                                      (Number(e.target.value) || 0) * 100,
                                    ),
                                  })
                                }
                              />
                            </td>
                          )}
                      <td className="p-2">
                        <button
                          type="button"
                          className="text-xs text-red-600 underline"
                          onClick={() =>
                            updateSet(setIndex, {
                              types: set.types.filter((_, i) => i !== typeIndex),
                            })
                          }
                          disabled={set.types.length <= 1}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="eh-btn-secondary px-3 py-1.5 text-sm"
              onClick={() =>
                updateSet(setIndex, {
                  types: [
                    ...set.types,
                    {
                      typeName: "",
                      category: null,
                      membershipTier: null,
                      currency: "usd",
                      ...(set.hasPricingPeriods
                        ? {
                            pricesByPeriod: {
                              early_bird: 0,
                              standard: 0,
                              on_site: 0,
                            },
                          }
                        : { pricePence: 0 }),
                    },
                  ],
                })
              }
            >
              Add ticket type
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className="eh-btn-secondary px-4 py-2 text-sm"
        onClick={addSet}
      >
        Add ticket set
      </button>
    </div>
  );
}
